import type { NavigationProp, ParamListBase } from "@react-navigation/core";
import React, { FC, useCallback, useRef, useState } from "react";
import { ActivityIndicator, ScrollView, StyleSheet, Text, TextInput, View } from "react-native";
import { useSelector } from "react-redux";
import { kRoute } from "../kRoute";
import { kColors } from "../kColors";
import { loginWithEmailAndPassword } from "../redux/user/actions";
import { kFonts } from "src/kFonts";
import { kStyleConst } from "src/kStyle";
import { PdButton, PdButtonLabel } from "src/components/forms/button";
import { useAppDispatch } from "src/redux";

const P01_LogIn: FC<{
  navigation: NavigationProp<ParamListBase>;
}> = ({ navigation }) => {
  const dispatch = useAppDispatch();
  const rIsLoggingUserIn = useSelector(({ UserReducer }) => UserReducer.isLoggingInLoading);
  const rIsMobile = useSelector(
    (state) => state.NavigationReducer.deviceSize === "xs" || state.NavigationReducer.deviceSize === "sm",
  );

  const [sEmail, _setEmail] = useState<string>("");
  const [sPassword, _setPassword] = useState<string>("");

  const emailRef = useRef<TextInput | null>(null);
  const passwordRef = useRef<TextInput | null>(null);

  const _handleLogin = useCallback(() => {
    dispatch(loginWithEmailAndPassword(sEmail, sPassword));
  }, [dispatch, sEmail, sPassword]);

  const _handleForgotPassword = useCallback(() => {
    navigation.navigate(kRoute.P01_03);
  }, [navigation]);

  const _navigateToApply = useCallback(() => {
    navigation.navigate(kRoute.P01_01);
  }, [navigation]);

  const _handleOnSubmitEditingEmail = useCallback(() => {
    if (sPassword.length === 0) {
      passwordRef.current?.focus();
    } else {
      _handleLogin();
    }
  }, [_handleLogin, sPassword.length]);

  const _handleOnSubmitEditingPassword = useCallback(() => {
    if (sEmail.length === 0) {
      emailRef.current?.focus();
    } else {
      _handleLogin();
    }
  }, [_handleLogin, sEmail.length]);

  if (rIsLoggingUserIn) {
    return (
      <View style={styles.gLoadingView}>
        <ActivityIndicator size="large" color={kColors.brandGreen} />
      </View>
    );
  }

  return (
    <ScrollView contentContainerStyle={rIsMobile ? [styles.px15, styles.py10] : [styles.px25, styles.py20]}>
      <View style={[styles.flexRow, styles.flexWrap, styles.alignCenter, styles.justifyBetween, styles.mb5]}>
        <View style={rIsMobile ? [styles.wFull, styles.mb5] : []}>
          <Text style={styles.h2}>Login</Text>
          <Text style={styles.body}>If you do not have an account, Apply Now</Text>
        </View>
        <PdButton onPress={_navigateToApply} variant="outline" size="lg" isFullWidth={rIsMobile}>
          <PdButtonLabel>Apply to Join</PdButtonLabel>
        </PdButton>
      </View>
      <View>
        <View style={styles.mb5}>
          <Text style={[styles.h3, styles.mb1]}>Email</Text>
          <TextInput
            ref={emailRef}
            onSubmitEditing={_handleOnSubmitEditingEmail}
            placeholder="User Email"
            value={sEmail}
            onChangeText={(value) => _setEmail(value.replace(/\s+/g, ""))}
            autoCompleteType="email"
            style={styles.inputLogin}
            placeholderTextColor={kColors.gray400}
          />
        </View>
        <View style={styles.mb5}>
          <Text style={[styles.h3, styles.mb1]}>Password</Text>
          <TextInput
            ref={passwordRef}
            onSubmitEditing={_handleOnSubmitEditingPassword}
            value={sPassword}
            onChangeText={(value) => _setPassword(value.replace(/\s+/g, ""))}
            placeholder="User Password"
            autoCompleteType="password"
            secureTextEntry={true}
            style={styles.inputLogin}
            placeholderTextColor={kColors.gray400}
          />
        </View>
        <View
          style={[
            styles.flexRow,
            styles.flexWrap,
            styles.alignCenter,
            styles.justifyBetween,
            rIsMobile ? [styles.flexReverse] : [],
          ]}
        >
          <PdButton onPress={_handleForgotPassword} size="lg" variant="link" isFullWidth={rIsMobile}>
            <PdButtonLabel>Forgot Password?</PdButtonLabel>
          </PdButton>
          <PdButton onPress={_handleLogin} size="lg" isFullWidth={rIsMobile} style={rIsMobile ? styles.mb5 : {}}>
            <PdButtonLabel>Login</PdButtonLabel>
          </PdButton>
        </View>
      </View>
    </ScrollView>
  );
};

export const P01 = React.memo(P01_LogIn);

const styles = StyleSheet.create({
  alignCenter: {
    alignItems: "center",
  },
  body: {
    ...kFonts.poppinsRegularLight,
    color: kColors.brand500,
    letterSpacing: 1,
    lineHeight: 25,
  },
  flexReverse: {
    flexDirection: "column-reverse",
  },
  flexRow: {
    flexDirection: "row",
  },
  flexWrap: {
    flexWrap: "wrap",
  },
  gLoadingView: {
    alignItems: "center",
    flex: 1,
    justifyContent: "center",
  },
  h2: {
    ...kFonts.ralewayLargeLight,
    color: kColors.brand500,
    letterSpacing: 1.1,
    lineHeight: 35,
  },
  h3: {
    ...kFonts.H3,
    color: kColors.darkGray,
    letterSpacing: 1.1,
    lineHeight: 30,
  },
  inputLogin: {
    borderBottomWidth: 1,
    borderColor: kColors.brandPink,
    flex: 1,
    height: 36,
    outlineColor: kColors.transparent,
    ...kFonts.poppinsRegularLight,
    letterSpacing: 1,
    lineHeight: 25,
  },
  justifyBetween: {
    justifyContent: "space-between",
  },
  mb1: {
    marginBottom: 1 * kStyleConst.kSpacing,
  },
  mb5: {
    marginBottom: 5 * kStyleConst.kSpacing,
  },
  px15: {
    paddingHorizontal: 15 * kStyleConst.kSpacing,
  },
  px25: {
    paddingHorizontal: 25 * kStyleConst.kSpacing,
  },
  py10: {
    paddingVertical: 10 * kStyleConst.kSpacing,
  },
  py20: {
    paddingVertical: 20 * kStyleConst.kSpacing,
  },
  wFull: {
    width: "100%",
  },
});
